import {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

const MOBILE_MAX = 991.98
const CLOSE_LOCK_MS = 650

type OptionRow = {
  value: string
  label: string
  disabled: boolean
}

type SheetState = {
  select: HTMLSelectElement
  options: OptionRow[]
  current: string
}

const isMobileLike = () => {
  if (typeof window === 'undefined') return false
  if (window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches) return true
  if (window.visualViewport && window.visualViewport.width <= MOBILE_MAX) return true
  return window.innerWidth <= MOBILE_MAX
}

const isSkippedSelect = (select: HTMLSelectElement) => {
  if (select.disabled || select.multiple) return true
  const size = Number(select.getAttribute('size') || 0)
  if (size > 1) return true
  return Boolean(
    select.closest('.ql-toolbar, .ql-container, .ql-snow, .quill, .ql-picker, .bo-select-sheet'),
  )
}

const readOptions = (select: HTMLSelectElement): OptionRow[] =>
  Array.from(select.options).map((opt) => ({
    value: opt.value,
    label: (opt.textContent || opt.label || opt.value || '').trim(),
    disabled: opt.disabled,
  }))

const commitValue = (select: HTMLSelectElement, value: string) => {
  const proto = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')
  if (proto?.set) {
    proto.set.call(select, value)
  } else {
    select.value = value
  }
  select.dispatchEvent(new Event('input', {bubbles: true}))
  select.dispatchEvent(new Event('change', {bubbles: true}))
}

const pointFromEvent = (event: Event): {x: number; y: number} | null => {
  if ('touches' in event) {
    const touch = (event as TouchEvent).touches[0] || (event as TouchEvent).changedTouches[0]
    if (touch) return {x: touch.clientX, y: touch.clientY}
  }
  if ('clientX' in event) {
    const mouse = event as MouseEvent
    return {x: mouse.clientX, y: mouse.clientY}
  }
  return null
}

const selectAtPoint = (x: number, y: number): HTMLSelectElement | null => {
  let found: HTMLSelectElement | null = null
  let best = Infinity
  document.querySelectorAll('select').forEach((node) => {
    if (!(node instanceof HTMLSelectElement) || isSkippedSelect(node)) return
    const rect = node.getBoundingClientRect()
    if (rect.width < 8 || rect.height < 8) return
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return
    const area = rect.width * rect.height
    if (area < best) {
      best = area
      found = node
    }
  })
  return found
}

const shouldInterceptSelect = (select: HTMLSelectElement) => {
  if (isMobileLike()) return true
  return getComputedStyle(select).pointerEvents === 'none'
}

const MobileSelectSheet = () => {
  const [sheet, setSheet] = useState<SheetState | null>(null)
  const [sheetLocked, setSheetLocked] = useState(false)
  const canCloseRef = useRef(false)
  const openTimerRef = useRef(0)
  const lastOpenedRef = useRef<HTMLSelectElement | null>(null)
  const sheetOpenRef = useRef(false)

  useEffect(() => {
    sheetOpenRef.current = Boolean(sheet)
  }, [sheet])

  useEffect(() => {
    const openSheet = (select: HTMLSelectElement) => {
      if (!select.isConnected) return
      canCloseRef.current = false
      sheetOpenRef.current = true
      lastOpenedRef.current = select
      window.clearTimeout(openTimerRef.current)
      setSheetLocked(true)
      setSheet({
        select,
        options: readOptions(select),
        current: select.value,
      })
      openTimerRef.current = window.setTimeout(() => {
        canCloseRef.current = true
        setSheetLocked(false)
      }, CLOSE_LOCK_MS)
    }

    const intercept = (event: Event) => {
      const target = event.target
      if (target instanceof Element && target.closest('.bo-select-sheet, .bo-select-hit')) return

      const point = pointFromEvent(event)
      if (!point) return
      const select = selectAtPoint(point.x, point.y)
      if (!select || !shouldInterceptSelect(select)) return

      event.preventDefault()
      event.stopPropagation()
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation()
      }
      try {
        select.blur()
      } catch {
        /* ignore */
      }
      if (lastOpenedRef.current === select && !canCloseRef.current) return
      openSheet(select)
    }

    const hits = new Map<HTMLSelectElement, HTMLButtonElement>()

    const placeHit = (select: HTMLSelectElement, btn: HTMLButtonElement) => {
      const rect = select.getBoundingClientRect()
      const inView =
        rect.width >= 8 &&
        rect.height >= 8 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight &&
        rect.right > 0 &&
        rect.left < window.innerWidth
      const active = isMobileLike() && inView && !sheetOpenRef.current
      btn.style.cssText = [
        'position:fixed',
        `left:${rect.left}px`,
        `top:${rect.top}px`,
        `width:${rect.width}px`,
        `height:${rect.height}px`,
        'margin:0',
        'padding:0',
        'border:0',
        'background:transparent',
        'opacity:0',
        'z-index:10040',
        `display:${active ? 'block' : 'none'}`,
        `pointer-events:${active ? 'auto' : 'none'}`,
        'cursor:pointer',
        '-webkit-tap-highlight-color:transparent',
      ].join(';')
    }

    const syncHits = () => {
      const live = new Set<HTMLSelectElement>()
      if (isMobileLike()) {
        document.querySelectorAll('select').forEach((node) => {
          if (!(node instanceof HTMLSelectElement) || isSkippedSelect(node)) return
          live.add(node)
          let btn = hits.get(node)
          if (!btn) {
            btn = document.createElement('button')
            btn.type = 'button'
            btn.className = 'bo-select-hit'
            btn.tabIndex = -1
            btn.setAttribute('aria-hidden', 'true')
            btn.addEventListener(
              'pointerdown',
              (event) => {
                event.preventDefault()
                event.stopPropagation()
                openSheet(node)
              },
              {passive: false},
            )
            document.body.appendChild(btn)
            hits.set(node, btn)
          }
          placeHit(node, btn)
        })
      }
      hits.forEach((btn, select) => {
        if (!live.has(select) || !select.isConnected) {
          btn.remove()
          hits.delete(select)
        }
      })
    }

    const listenerOpts: AddEventListenerOptions = {capture: true, passive: false}
    document.addEventListener('pointerdown', intercept, listenerOpts)
    document.addEventListener('touchstart', intercept, listenerOpts)

    const proto = window.HTMLSelectElement.prototype as HTMLSelectElement & {
      showPicker?: () => void
    }
    const originalShowPicker = proto.showPicker
    if (typeof originalShowPicker === 'function') {
      proto.showPicker = function showPickerOverride(this: HTMLSelectElement) {
        if (shouldInterceptSelect(this) && !isSkippedSelect(this)) {
          openSheet(this)
          return
        }
        return originalShowPicker.call(this)
      }
    }

    const observer = new MutationObserver(() => syncHits())
    observer.observe(document.body, {childList: true, subtree: true})
    window.addEventListener('resize', syncHits)
    window.addEventListener('scroll', syncHits, true)
    window.visualViewport?.addEventListener('resize', syncHits)
    window.visualViewport?.addEventListener('scroll', syncHits)
    syncHits()
    const pulse = window.setInterval(syncHits, 500)

    return () => {
      document.removeEventListener('pointerdown', intercept, listenerOpts)
      document.removeEventListener('touchstart', intercept, listenerOpts)
      if (typeof originalShowPicker === 'function') {
        proto.showPicker = originalShowPicker
      }
      observer.disconnect()
      window.removeEventListener('resize', syncHits)
      window.removeEventListener('scroll', syncHits, true)
      window.visualViewport?.removeEventListener('resize', syncHits)
      window.visualViewport?.removeEventListener('scroll', syncHits)
      window.clearInterval(pulse)
      window.clearTimeout(openTimerRef.current)
      hits.forEach((btn) => btn.remove())
      hits.clear()
    }
  }, [])

  useEffect(() => {
    if (!sheet) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        canCloseRef.current = true
        lastOpenedRef.current = null
        setSheet(null)
      }
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [sheet])

  if (!sheet || typeof document === 'undefined') return null

  const finishClose = () => {
    if (!canCloseRef.current) return
    canCloseRef.current = true
    lastOpenedRef.current = null
    sheetOpenRef.current = false
    setSheetLocked(false)
    setSheet(null)
  }

  return createPortal(
    <div
      className={`bo-select-sheet${sheetLocked ? ' is-locked' : ''}`}
      role='presentation'
      onClick={(event) => {
        if (sheetLocked || !canCloseRef.current) return
        if (event.target === event.currentTarget) {
          finishClose()
        }
      }}
    >
      <div className='bo-select-sheet__panel' role='listbox'>
        <div className='bo-select-sheet__handle' />
        <div className='bo-select-sheet__list'>
          {sheet.options.map((opt, index) => {
            const selected = opt.value === sheet.current
            return (
              <button
                key={`${opt.value}-${index}`}
                type='button'
                role='option'
                aria-selected={selected}
                disabled={opt.disabled}
                className={`bo-select-sheet__option${selected ? ' is-selected' : ''}`}
                onClick={() => {
                  if (sheetLocked || !canCloseRef.current) return
                  if (!opt.disabled) {
                    commitValue(sheet.select, opt.value)
                  }
                  finishClose()
                }}
              >
                {opt.label || '—'}
              </button>
            )
          })}
        </div>
        <button type='button' className='bo-select-sheet__close' onClick={finishClose}>
          Fermer
        </button>
      </div>
    </div>,
    document.body,
  )
}

export {MobileSelectSheet}
